
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export function useExpertStatus(applicationId: string, isApproved: boolean) {
  const [expertStatus, setExpertStatus] = useState<'active' | 'inactive' | 'draft' | 'pending_approval' | 'suspended' | null>(null);
  const [loading, setLoading] = useState(false);
  const [expertId, setExpertId] = useState<string | null>(null);

  useEffect(() => {
    if (isApproved) {
      fetchExpertStatus();
    } else {
      // Reset state when not approved
      setExpertStatus(null);
      setExpertId(null);
    }
  }, [isApproved, applicationId]);

  const fetchExpertStatus = async () => {
    try {
      console.log("🔍 Fetching expert status for application:", applicationId);
      const { data, error } = await supabase
        .from("consultation_experts")
        .select("id, status, google_calendar_link")
        .eq("application_id", applicationId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error("❌ Error fetching expert status:", error);
        throw error;
      }
      
      console.log("📊 Expert data:", data);
      setExpertStatus(data?.status || null);
      setExpertId(data?.id || null);
    } catch (error) {
      console.error("💥 Error fetching expert status:", error);
    }
  };

  const handleToggleExpertStatus = async () => {
    if (!isApproved) {
      toast.error("Application must be approved first");
      return;
    }

    if (!expertId) {
      toast.error("Expert profile not found. Please refresh the page.");
      return;
    }

    setLoading(true);
    try {
      console.log("🔄 Toggling expert status from:", expertStatus);
      
      // Check if expert has booking link before publishing
      if (expertStatus !== 'active') {
        const { data: expertData, error: fetchError } = await supabase
          .from("consultation_experts")
          .select("google_calendar_link")
          .eq("id", expertId)
          .single();

        if (fetchError) {
          console.error("❌ Error fetching expert data:", fetchError);
          throw fetchError;
        }

        if (!expertData.google_calendar_link || expertData.google_calendar_link.trim() === '') {
          toast.error("Cannot publish expert without a booking link. Please add a booking link in the 'Booking Link Management' section above.");
          setLoading(false);
          return;
        }
      }
      
      const newStatus = expertStatus === 'active' ? 'inactive' : 'active';
      console.log("🎯 Setting new status to:", newStatus);
      
      const { error } = await supabase
        .from("consultation_experts")
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq("id", expertId);

      if (error) {
        console.error("❌ Error updating expert status:", error);
        throw error;
      }

      console.log("✅ Expert status updated successfully");
      setExpertStatus(newStatus);
      toast.success(`Expert profile ${newStatus === 'active' ? 'published' : 'unpublished'} successfully`);
      
      // Refresh the data to ensure consistency
      setTimeout(() => {
        fetchExpertStatus();
      }, 500);
      
    } catch (error) {
      console.error("💥 Error toggling expert status:", error);
      toast.error("Failed to update expert status");
    } finally {
      setLoading(false);
    }
  };

  return {
    expertStatus,
    expertId,
    loading,
    handleToggleExpertStatus,
    refreshStatus: fetchExpertStatus
  };
}
